import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useLexicalEditable } from '@lexical/react/useLexicalEditable';
import { ActionIcon } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import {
  $getNodeByKey,
  $getNearestNodeFromDOMNode,
  isHTMLElement,
  NodeKey,
  NodeMutation,
} from 'lexical';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  $dismissSuggestionMark,
  $isSuggestionMarkNode,
  $sourceSuggestionMark,
  SuggestionMarkNode,
} from '../nodes/SuggestionMarkNode';
import {
  $dismissSuggestion,
  $isSuggestionNode,
  $sourceSuggestion,
  SuggestionNode,
} from '../nodes/SuggestionNode';
import { SUGGESTION_COLORS } from '../nodes/suggestion-colors';
import { Source } from '../types';
import { $containingSuggestionOrMark } from '../utils';

type Props = {
  anchorElem: HTMLElement;
  showMarkers: boolean;
  hiddenSources?: Source[];
};

export default function SuggestionDismissPlugin({ anchorElem, showMarkers, hiddenSources }: Props) {
  const isEditable = useLexicalEditable();

  return isEditable
    ? createPortal(
        <SuggestionDismissContainer
          anchorElem={anchorElem}
          showMarkers={showMarkers}
          hiddenSources={hiddenSources}
        />,
        anchorElem,
      )
    : null;
}

type HoveredSuggestion = {
  domElement: HTMLElement;
  nodeKey: NodeKey;
  source: Source;
};

const BUTTON_SIZE = 24;
const HIT_AREA_OVERLAP = 14;
const TALL_THRESHOLD = 60;
const DISMISS_DELAY = 225;

function SuggestionDismissContainer({ anchorElem, showMarkers, hiddenSources }: Props) {
  const [editor] = useLexicalComposerContext();
  const [hoveredSuggestion, setHoveredSuggestion] = useState<HoveredSuggestion | null>(null);
  const [buttonPos, setButtonPos] = useState({ top: 0, left: 0 });
  const buttonHoveredRef = useRef(false);
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelDismissTimer = () => {
    if (dismissTimerRef.current) {
      clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }
  };

  const scheduleDismiss = () => {
    cancelDismissTimer();
    dismissTimerRef.current = setTimeout(() => {
      dismissTimerRef.current = null;
      setHoveredSuggestion(null);
    }, DISMISS_DELAY);
  };

  useEffect(() => {
    const rootElement = editor.getRootElement();
    if (!rootElement) return;

    const handleMouseOver = (event: MouseEvent) => {
      if (!isHTMLElement(event.target)) return;

      const target = event.target as HTMLElement;
      let suggestionElem = target.closest<HTMLElement>('[data-suggestion="true"]');

      if (!suggestionElem) {
        const cellWithSuggestion = target.closest<HTMLElement>('[data-suggestion-cell="true"]');
        if (cellWithSuggestion) {
          suggestionElem = cellWithSuggestion.querySelector<HTMLElement>(
            '[data-suggestion="true"]',
          );
        }
      }

      if (!suggestionElem) {
        if (!buttonHoveredRef.current) {
          scheduleDismiss();
        }
        return;
      }

      cancelDismissTimer();

      editor.read(() => {
        if (!suggestionElem) return;
        const elem = suggestionElem;
        const node = $getNearestNodeFromDOMNode(elem);
        const match = $containingSuggestionOrMark(node);
        if (match) {
          const source = $isSuggestionNode(match)
            ? $sourceSuggestion(match)
            : $sourceSuggestionMark(match);
          setHoveredSuggestion(prev => {
            if (prev?.nodeKey === match.getKey()) return prev;
            return { domElement: elem, nodeKey: match.getKey(), source };
          });
        }
      });
    };

    const handleMouseOut = (event: MouseEvent) => {
      const relatedTarget = event.relatedTarget as HTMLElement | null;
      if (!relatedTarget) {
        scheduleDismiss();
        return;
      }

      if (
        relatedTarget.closest('[data-suggestion="true"]') ||
        relatedTarget.closest('[data-suggestion-cell="true"]') ||
        relatedTarget.closest('[data-suggestion-dismiss]')
      ) {
        return;
      }

      scheduleDismiss();
    };

    rootElement.addEventListener('mouseover', handleMouseOver);
    rootElement.addEventListener('mouseout', handleMouseOut);

    return () => {
      rootElement.removeEventListener('mouseover', handleMouseOver);
      rootElement.removeEventListener('mouseout', handleMouseOut);
      cancelDismissTimer();
    };
  }, [editor]);

  useEffect(() => {
    const callback = (mutations: Map<NodeKey, NodeMutation>) => {
      setHoveredSuggestion(prev => {
        if (prev && mutations.get(prev.nodeKey) === 'destroyed') return null;
        return prev;
      });
    };

    const unregisterSuggestion = editor.registerMutationListener(SuggestionNode, callback);
    const unregisterMark = editor.registerMutationListener(SuggestionMarkNode, callback);

    return () => {
      unregisterSuggestion();
      unregisterMark();
    };
  }, [editor]);

  useEffect(() => {
    if (!hoveredSuggestion) return;

    const updatePosition = () => {
      const cell = hoveredSuggestion.domElement.closest<HTMLElement>(
        '.editor-table-cell, .editor-table-cell-header',
      );
      const positionTarget =
        cell?.dataset.suggestionCell === 'true' ? cell : hoveredSuggestion.domElement;
      const suggestionRect = positionTarget.getBoundingClientRect();
      const anchorRect = anchorElem.getBoundingClientRect();

      const isTall = suggestionRect.height > TALL_THRESHOLD;
      const verticalOffset = isTall
        ? suggestionRect.bottom - anchorRect.top - BUTTON_SIZE - 4
        : suggestionRect.top - anchorRect.top + (suggestionRect.height - BUTTON_SIZE) / 2;

      setButtonPos({
        top: verticalOffset,
        left: suggestionRect.right - anchorRect.left - HIT_AREA_OVERLAP + 2,
      });
    };

    updatePosition();

    const scrollParent = anchorElem.parentElement;
    scrollParent?.addEventListener('scroll', updatePosition);

    return () => {
      scrollParent?.removeEventListener('scroll', updatePosition);
    };
  }, [hoveredSuggestion, anchorElem]);

  const handleDismiss = () => {
    if (!hoveredSuggestion) return;

    editor.update(() => {
      const node = $getNodeByKey(hoveredSuggestion.nodeKey);
      if ($isSuggestionNode(node)) {
        $dismissSuggestion(node);
      } else if ($isSuggestionMarkNode(node)) {
        $dismissSuggestionMark(node);
      }
    });

    setHoveredSuggestion(null);
  };

  if (!showMarkers || !hoveredSuggestion || hiddenSources?.includes(hoveredSuggestion.source))
    return null;

  return (
    <div
      data-suggestion-dismiss=""
      style={{
        position: 'absolute',
        top: buttonPos.top,
        left: buttonPos.left,
        zIndex: 10,
        pointerEvents: 'auto',
        paddingLeft: HIT_AREA_OVERLAP,
        display: 'flex',
        alignItems: 'center',
      }}
      onMouseEnter={() => {
        buttonHoveredRef.current = true;
        cancelDismissTimer();
      }}
      onMouseLeave={() => {
        buttonHoveredRef.current = false;
        scheduleDismiss();
      }}
    >
      <ActionIcon
        variant="subtle"
        color="black"
        size={BUTTON_SIZE}
        radius="xl"
        onClick={handleDismiss}
        style={{
          backgroundColor: SUGGESTION_COLORS[hoveredSuggestion.source].unfocused,
        }}
      >
        <IconX size={12} />
      </ActionIcon>
    </div>
  );
}
